import React, { useMemo } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  useColorModeValue,
  Flex,
  Icon,
  Button,
  HStack,
  Select,
  VStack,
  Divider,
} from "@chakra-ui/react";
import { MdBolt, MdLocationOn, MdCheckCircle, MdCancel } from "react-icons/md";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useTable, usePagination } from "react-table";
import { useNavigate } from "react-router-dom";

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "orange" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

const ChargingSessionsTable = ({ sessions }) => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const labelColor = useColorModeValue("gray.600", "gray.400");

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusConfig = (status) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return { colorScheme: "green", icon: MdCheckCircle };
      case "ONGOING":
        return { colorScheme: "orange", icon: MdBolt };
      case "FAILED":
      case "STOPPED":
        return { colorScheme: "red", icon: MdCancel };
      default:
        return { colorScheme: "gray", icon: MdBolt };
    }
  };

  const columns = useMemo(
    () => [
      { Header: "Session ID", accessor: "Id" },
      { Header: "Mobile Number", accessor: "MobileNumber" },
      { Header: "Start Time", accessor: "StartTime" },
      { Header: "End Time", accessor: "EndTime" },
      { Header: "Station", accessor: "EvseId" },
      { Header: "Energy (kWh)", accessor: "UnitsConsumed" },
      { Header: "Cost (₹)", accessor: "TotalCostWithTax" },
      { Header: "Status", accessor: "Status" },
    ],
    []
  );

  const data = useMemo(() => {
    if (!sessions) return [];
    // Sort by StartTime in descending order (latest first)
    return [...sessions].sort((a, b) => {
      const dateA = new Date(a.StartTime);
      const dateB = new Date(b.StartTime);
      return dateB - dateA; // Descending order
    });
  }, [sessions]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    gotoPage,
    setPageSize,
    canPreviousPage,
    canNextPage,
    pageCount,
    nextPage,
    previousPage,
  } = tableInstance;

  if (!data || data.length === 0) {
    return (
      <Box bg={cardBg} p={8} borderRadius="lg" textAlign="center" border="1px solid" borderColor={borderColor}>
        <Icon as={MdBolt} boxSize={12} color="gray.300" mb={3} />
        <Text color={labelColor}>No charging sessions found</Text>
      </Box>
    );
  }

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      boxShadow="sm"
    >
      {/* Desktop Table View */}
      <Box display={{ base: "none", lg: "block" }} overflowX="auto">
        <Table {...getTableProps()} variant="simple">
          <Thead bg="rgba(245, 125, 54, 0.05)">
            {headerGroups.map((headerGroup, index) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column, index) => (
                  <Th
                    {...column.getHeaderProps()}
                    key={index}
                    color={labelColor}
                    fontSize="xs"
                    textTransform="uppercase"
                  >
                    {column.render("Header")}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              const session = row.original;
              const statusConfig = getStatusConfig(session.Status);
              return (
                <Tr
                  {...row.getRowProps()}
                  key={session.Id || index}
                  _hover={{ bg: "rgba(245, 125, 54, 0.02)", cursor: "pointer" }}
                  transition="all 0.2s"
                  onClick={() => navigate(`/admin/charging_management/summary/${session.Id}`)}
                >
                  <Td>
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                      {session?.Id}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                      {session?.MobileNumber || session?.IdTag || "N/A"}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color={labelColor}>
                      {formatDate(session?.StartTime)}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color={labelColor}>
                      {formatDate(session?.EndTime)}
                    </Text>
                  </Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Icon as={MdLocationOn} color="#f57d36" boxSize={4} />
                      <Text fontSize="sm" color={textColor}>
                        {session?.StationName || session?.EvseId || "N/A"}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                      {session?.UnitsConsumed?.toFixed(2) || "0.00"}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" fontWeight="bold" color="#f57d36">
                      ₹{session?.TotalCostWithTax?.toFixed(2) || "0.00"}
                    </Text>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={statusConfig.colorScheme}
                      fontSize="xs"
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      <Flex align="center" gap={1}>
                        <Icon as={statusConfig.icon} boxSize={3} />
                        {session.Status || "N/A"}
                      </Flex>
                    </Badge>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Mobile Card View */}
      <VStack display={{ base: "flex", lg: "none" }} spacing={2} p={2} align="stretch">
        {page.map((row, index) => {
          prepareRow(row);
          const session = row.original;
          const statusConfig = getStatusConfig(session.Status);
          return (
            <Box
              key={session.Id || index}
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              p={2}
              _hover={{ borderColor: "#f57d36", boxShadow: "md", cursor: "pointer" }}
              transition="all 0.2s"
              onClick={() => navigate(`/admin/charging_management/summary/${session.Id}`)}
            >
              {/* Header: Session ID and Status */}
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="sm" fontWeight="bold" color={textColor}>
                  {session?.Id}
                </Text>
                <Badge
                  colorScheme={statusConfig?.colorScheme}
                  fontSize="2xs"
                  px={1.5}
                  py={0.5}
                  borderRadius="md"
                >
                  <Flex align="center" gap={0.5}>
                    <Icon as={statusConfig?.icon} boxSize={2.5} />
                    <Text fontSize="2xs">{session?.Status || "N/A"}</Text>
                  </Flex>
                </Badge>
              </Flex>

              <Divider mb={2} />

              {/* Mobile Number - Full Width */}
              <Box
                p={1.5}
                bg="rgba(245, 125, 54, 0.05)"
                borderRadius="md"
                border="1px solid"
                borderColor={borderColor}
                mb={1.5}
              >
                <Text fontSize="2xs" color={labelColor} mb={0.5}>
                  MOBILE NUMBER
                </Text>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>
                  {session?.MobileNumber || session?.IdTag || "N/A"}
                </Text>
              </Box>

              {/* Two Column Layout - Start/End Time */}
              <Flex gap={1.5} mb={1.5}>
                {/* Start Time */}
                <Box
                  flex={1}
                  p={1.5}
                  bg="rgba(245, 125, 54, 0.05)"
                  borderRadius="md"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Text fontSize="2xs" color={labelColor} mb={0.5}>
                    START TIME
                  </Text>
                  <Text fontSize="xs" fontWeight="bold" color={textColor}>
                    {formatDate(session?.StartTime)}
                  </Text>
                </Box>

                {/* End Time */}
                <Box
                  flex={1}
                  p={1.5}
                  bg="rgba(245, 125, 54, 0.05)"
                  borderRadius="md"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Text fontSize="2xs" color={labelColor} mb={0.5}>
                    END TIME
                  </Text>
                  <Text fontSize="xs" fontWeight="bold" color={textColor}>
                    {formatDate(session?.EndTime)}
                  </Text>
                </Box>
              </Flex>

              {/* Three Column Layout - Station, Energy, Cost */}
              <Flex gap={1.5}>
                {/* Station */}
                <Box
                  flex={1}
                  p={1.5}
                  bg="rgba(245, 125, 54, 0.05)"
                  borderRadius="md"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Flex align="center" gap={1}>
                    <Icon as={MdLocationOn} color="#f57d36" boxSize={3} />
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="2xs" color={labelColor} fontWeight="semibold">
                        STATION
                      </Text>
                      <Text fontSize="xs" fontWeight="bold" color={textColor}>
                        {session?.StationName || session?.EvseId || "N/A"}
                      </Text>
                    </VStack>
                  </Flex>
                </Box>

                {/* Energy */}
                <Box
                  flex={1}
                  p={1.5}
                  bg="rgba(245, 125, 54, 0.05)"
                  borderRadius="md"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Text fontSize="2xs" color={labelColor} mb={0.5}>
                    ENERGY (kWh)
                  </Text>
                  <Text fontSize="xs" fontWeight="bold" color={textColor}>
                    {session?.UnitsConsumed?.toFixed(2) || "0.00"}
                  </Text>
                </Box>

                {/* Total Cost */}
                <Box
                  flex={1}
                  p={1.5}
                  bg="rgba(245, 125, 54, 0.05)"
                  borderRadius="md"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Text fontSize="2xs" color={labelColor} mb={0.5}>
                    TOTAL COST
                  </Text>
                  <Text fontSize="xs" fontWeight="bold" color="#f57d36">
                    ₹{session?.TotalCostWithTax?.toFixed(2) || "0.00"}
                  </Text>
                </Box>
              </Flex>
            </Box>
          );
        })}
      </VStack>

      {/* Pagination */}
      <Flex
        p={4}
        justify="space-between"
        align="center"
        borderTop="1px solid"
        borderColor={borderColor}
        flexWrap="wrap"
        gap={4}
      >
        <HStack>
          <Select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            size="sm"
            borderRadius="md"
            w="120px"
          >
            {[5, 10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Select>
          <Text fontSize="sm" color="gray.500">
            Showing {page.length} of {data.length} results
          </Text>
        </HStack>

        <HStack spacing={2}>
          <PaginationButton
            isDisabled={!canPreviousPage}
            onClick={() => gotoPage(0)}
          >
            <ChevronLeftIcon boxSize={4} mr={1} />
            First
          </PaginationButton>

          <PaginationButton
            isDisabled={!canPreviousPage}
            onClick={() => previousPage()}
          >
            <ChevronLeftIcon boxSize={4} />
          </PaginationButton>

          {pageCount <= 5 ? (
            [...Array(pageCount)].map((_, i) => (
              <PaginationButton
                key={i}
                isActive={pageIndex === i}
                onClick={() => gotoPage(i)}
              >
                {i + 1}
              </PaginationButton>
            ))
          ) : (
            <>
              {pageIndex > 1 && (
                <PaginationButton onClick={() => gotoPage(0)}>
                  1
                </PaginationButton>
              )}

              {pageIndex > 2 && <Text>...</Text>}

              {pageIndex > 0 && (
                <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                  {pageIndex}
                </PaginationButton>
              )}

              <PaginationButton isActive onClick={() => gotoPage(pageIndex)}>
                {pageIndex + 1}
              </PaginationButton>

              {pageIndex < pageCount - 1 && (
                <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                  {pageIndex + 2}
                </PaginationButton>
              )}

              {pageIndex < pageCount - 3 && <Text>...</Text>}

              {pageIndex < pageCount - 2 && (
                <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                  {pageCount}
                </PaginationButton>
              )}
            </>
          )}

          <PaginationButton
            isDisabled={!canNextPage}
            onClick={() => nextPage()}
          >
            <ChevronRightIcon boxSize={4} />
          </PaginationButton>

          <PaginationButton
            isDisabled={!canNextPage}
            onClick={() => gotoPage(pageCount - 1)}
          >
            Last
            <ChevronRightIcon boxSize={4} ml={1} />
          </PaginationButton>
        </HStack>
      </Flex>
    </Box>
  );
};

export default ChargingSessionsTable;
